import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'

import { AuthForm } from '../AuthForm'
import { AuthSuccessText } from '../AuthSuccessText'

import { Button, FormField, Spacer, TextInput, Typography } from '@percent/lemonade'
import { useMutation } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { passwordRequirementRegExp } from '@percent/partner-dashboard/common/library/utility/validation'
import { FieldError } from '@percent/partner-dashboard/common/components'

export function SetPassword() {
  const { authService } = useServices()
  const { search } = useLocation()
  const hash = new URLSearchParams(search).get('userHash')
  const [{ success, error, errorMessage, isLoading }, { apiFunc: setPassword }] = useMutation(authService.setPassword)
  const navigate = useNavigate()
  const { t } = useTranslation()

  // The backend is the single gate (flag `be_require_two_fa_for_password_reset`): it only asks for a
  // code when 2FA is enforced, so the field simply follows the error the API returns.
  const otpPrompt = error?.code === 'auth/requires_two_fa_reset_password'
  const twoFaRequired = otpPrompt || error?.code === 'auth/two_fa_invalid_otp_code'

  // The first OTP prompt is an expected next step, not a failure — don't surface it as an error.
  // Real problems (invalid code, rate limits, expired link, …) still show.
  const displayErrorMessage = otpPrompt ? undefined : errorMessage

  useEffect(() => {
    if (!hash) {
      navigate('/signin')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  const formik = useFormik({
    initialValues: {
      password: '',
      otpCode: ''
    },
    validationSchema: () =>
      object().shape({
        password: string()
          .min(8, t('form.minimum8Characters'))
          .max(64)
          .matches(passwordRequirementRegExp, t('errorMessage.passwordRequirement'))
          .required('Required'),
        otpCode: twoFaRequired
          ? string()
              .required('Required')
              .matches(/^\d{6}$/, t('errorMessage.max6Digit'))
          : string()
      }),
    onSubmit: ({ password, otpCode }: { password: string; otpCode: string }) => {
      setPassword({ password, hash: hash as string, otpCode: otpCode || undefined })
    }
  })

  const { errors, values, touched, handleChange, handleBlur, handleSubmit } = formik

  return (
    <AuthForm heading={t('typography.setPassword')} handleSubmit={handleSubmit}>
      {success ? (
        <AuthSuccessText text={t('typography.passwordSetSuccessfully')} showSignInLink={false} showSignInButton />
      ) : (
        <>
          <FormikProvider value={formik}>
            <FormField
              showStrenghtMeter
              label="Password"
              status={touched.password && errors.password ? 'danger' : 'default'}
              statusMessage={errors.password}
              data-testid="password"
            >
              <TextInput
                type="password"
                name="password"
                placeholder="Enter your password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
            </FormField>
            {twoFaRequired && (
              <>
                <Spacer size={4} axis="vertical" />
                <Typography variant="default-bold">{t('form.twoStepVerification')}</Typography>
                <Spacer size={2} axis="vertical" />
                <FormField
                  label={t('form.enterSixDigit')}
                  instructions={t('form.twoFaResetInstructions')}
                  status={touched.otpCode && errors.otpCode ? 'danger' : 'default'}
                  statusMessage={touched.otpCode ? errors.otpCode : undefined}
                  data-testid="otpCode"
                >
                  <TextInput
                    name="otpCode"
                    inputMode="numeric"
                    placeholder={t('form.placeholderSixDigit')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.otpCode}
                    autoComplete="one-time-code"
                  />
                </FormField>
              </>
            )}
          </FormikProvider>
          {displayErrorMessage && <FieldError error={displayErrorMessage} />}
          <Spacer size={5} axis="vertical" />
          <Button stretch size="large" data-testid="auth-active-button" loading={isLoading} type="submit">
            {t('button.setPassword')}
          </Button>
        </>
      )}
    </AuthForm>
  )
}
